import React, { useEffect } from 'react';
import { Box, Container, Typography, Stack, Button, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import TrendsPage from './TrendsPage';
import Footer from './Footer';
import SEO from './SEO';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const cases = [
  { title: 'Bright Horizons', sub: 'Manhattan, NYC', idx: 0 },
  { title: 'Affinity Education', sub: 'New South Wales, AU', idx: 1 },
  { title: 'Maranatha Christian', sub: 'Florence, SC', idx: 2 },
  { title: 'Centennial, CO', sub: '51 counts, toddlers', idx: 3 },
  { title: 'New Horizon Academy', sub: 'Rochester, MN', idx: 4 },
  { title: 'Little Blessings', sub: 'Bainbridge, GA', idx: 5 },
  { title: 'Blanding, Utah', sub: 'Cameras caught it — too late', idx: 6 },
  { title: 'Mather Childcare', sub: 'Sacramento, CA', idx: 7 },
  { title: 'Punkin\' Doodles', sub: 'Lindale, TX — 134 incidents', idx: 8 },
];

const caseData = [
  {
    title: 'Bright Horizons, Manhattan',
    period: 'September 2023 – February 2025',
    image: '/brooklyn-daycare.png',
    summary: 'Three daycare workers at a prestigious Manhattan branch of Bright Horizons were arrested and charged with multiple counts of child endangerment and assault after a months-long pattern of abuse against at least nine children (ages 1–2) was uncovered by the Manhattan District Attorney\'s Office.',
    acts: [
      'Packing tape placed over a toddler\'s mouth and nose.',
      'Children dragged by hair and arms.',
      'Force-fed ginger shots and restrained to chairs.',
      'Hit on the head with a metal bottle.',
      'Sprayed in the face with bleach solution.',
    ],
    outcomes: [
      'Public outrage and national media coverage',
      'Severe reputational damage to the Bright Horizons brand',
      'Legal repercussions for all three accused workers',
      'Operational shutdown of the facility',
      'Ongoing legal proceedings',
    ],
    source: { label: 'Read full article', url: 'https://people.com/bright-horizons-daycare-workers-accused-abusing-kids-11772970?utm_source=chatgpt.com' },
    prevention: [
      { act: 'Packing tape placed over a toddler\'s mouth and nose', how: 'Systematic video analysis would have detected this dangerous act in real time, triggering an immediate alert for intervention.' },
      { act: 'Children dragged by hair and arms', how: 'SMCLA\'s AI is trained to recognize aggressive movements, flagging and recording such incidents for review and action.' },
      { act: 'Force-fed ginger shots and restrained to chairs', how: 'Unusual feeding and restraint behaviors would be identified and escalated to management for prompt investigation.' },
      { act: 'Hit on the head with a metal bottle', how: 'Physical strikes are detected by the system, ensuring evidence is captured and authorities are notified.' },
      { act: 'Sprayed in the face with bleach solution', how: 'Any hazardous or abusive action is recognized, with video evidence provided for immediate response and protection.' },
    ],
  },
  {
    title: 'Affinity Education, Australia',
    period: 'October 2024 – July 2025',
    image: '/australia-daycare.png',
    summary: 'A 51-year-old childcare worker at an Affinity Education centre in New South Wales, Australia, was charged with multiple counts of child sexual abuse involving eight alleged victims under the age of 10. The case triggered national debate, urgent safety reviews, and new security measures across the childcare sector.',
    acts: [
      'Eight counts of intentionally sexually touching a child under 10 years old.',
      'One count of carrying out a sexual act with another without consent.',
    ],
    outcomes: [
      'Worker charged, pleads not guilty',
      'Eight alleged victims identified',
      'National debate and urgent safety review',
      'CCTV, staff protocols, and law reforms fast-tracked',
    ],
    source: { label: 'Read full article', url: 'https://www.theguardian.com/australia-news/2025/jul/09/affinity-education-childcare-worker-facing-multiple-child-sexual-abuse-charges-ntwnfb' },
    prevention: [
      { act: 'Sexual abuse of children under 10', how: 'SMCLA\'s video analysis and strict access controls would have detected and prevented inappropriate contact, with alerts for any suspicious behavior.' },
      { act: 'Sexual act without consent', how: 'Real-time monitoring would have provided evidence and enabled immediate intervention, protecting children and supporting prosecution.' },
    ],
  },
  {
    title: 'Maranatha Christian School, Florence, SC',
    period: 'January 2025',
    image: '/country-daycare.png',
    summary: 'A 73-year-old daycare worker was arrested and denied bond after allegedly slamming a child\'s head into a crib, covering the child\'s head with a blanket and pressing down, then dragging the child out of the room. Three other staff members were arrested for failing to report.',
    acts: [
      'Child\'s head slammed into a crib.',
      'Child\'s head covered with a blanket and pressed down for a "length of time."',
      'Child dragged out of the room.',
      'Child knocked over on the playground and dropped from waist height onto their head.',
    ],
    outcomes: [
      'Worker arrested and denied bond',
      'Three additional staff arrested for failing to report',
      'Public outrage and media attention',
      'Ongoing legal proceedings',
    ],
    source: { label: 'Read full article', url: 'https://www.wistv.com/2025/02/11/bond-denied-daycare-worker-florence-area-private-school-accused-child-abuse/' },
    prevention: [
      { act: 'Head slammed into crib', how: 'SMCLA\'s video analysis would have detected this violent act instantly, alerting management and authorities for immediate intervention.' },
      { act: 'Head covered and pressed down', how: 'Unusual and dangerous restraint would be flagged, with video evidence for investigation and protection.' },
      { act: 'Child dragged out of room', how: 'Aggressive handling is recognized and escalated for review and action.' },
      { act: 'Dropped on head on playground', how: 'Falls and impacts are detected, ensuring prompt response and accountability.' },
    ],
  },
  {
    title: 'Centennial, Colorado (2025)',
    period: 'Discovered and charged 2025',
    image: '/country-daycare.png',
    summary: 'Brandon Vigil, 32, a preschool teacher, was charged with 51 criminal counts of child abuse and harassment against 2-year-old toddlers. Investigators documented "many instances of harmful, degrading, and humiliating actions" carried out systematically across multiple sessions.',
    acts: [
      'Repeated harmful, degrading, and humiliating treatment of toddlers.',
      'Systematic pattern of abuse across multiple sessions.',
      '51 individual criminal counts — the highest count in this regional case history.',
    ],
    outcomes: [
      '51 criminal counts of child abuse and harassment filed',
      'Worker arrested; families of multiple children affected',
      'State investigation opened into facility supervision practices',
      'Significant enrollment losses and reputational damage',
    ],
    source: { label: 'Source', url: '#' },
    prevention: [
      { act: 'Repeated degrading behavior across sessions', how: 'SMCLA\'s multi-session pattern detection would have flagged this worker\'s behavioral deviation from baseline within the first few incidents — not at count 51.' },
      { act: 'Systematic abuse of the same group of toddlers', how: 'AI tracking of physical contact patterns with specific children would have surfaced the targeting pattern, triggering an owner alert and immediate review.' },
      { act: 'Degrading language and emotional abuse', how: 'SMCLA\'s audio AI would have detected the vocal patterns of repeated humiliation, flagging the worker before the charge count reached double digits.' },
    ],
    note: 'Pattern detection across sessions is SMCLA\'s core strength. 51 counts — physical and verbal — means 51 missed opportunities. We would have caught it early.',
  },
  {
    title: 'New Horizon Academy, Rochester, MN (2025)',
    period: 'Charged 2025; investigation ongoing',
    image: '/daycare-woman-child.png',
    summary: 'Alexis Kemp, 25, was charged with two counts of malicious punishment of children. At least two infants under 4 were victims: one had bruising across 9 different body areas, the other sustained a leg fracture. The investigation expanded as additional victims were identified.',
    acts: [
      'Malicious punishment of children under 4 years of age.',
      'One infant with bruising documented across 9 separate body areas.',
      'One infant with a leg fracture consistent with physical abuse.',
    ],
    outcomes: [
      'Two criminal counts filed; investigation expanded to additional victims',
      'Infant sustained leg fracture — permanent injury risk',
      'Nine-area bruising pattern on a second infant victim',
      'New Horizon Academy under regulatory scrutiny',
    ],
    source: { label: 'Source', url: '#' },
    prevention: [
      { act: 'Physical contact anomalies triggering injury', how: 'SMCLA\'s force and velocity detection would have flagged aggressive physical handling on the first incident — before any injury occurred.' },
      { act: '9-area bruising pattern across sessions', how: 'Multi-session physical contact tracking would have identified this worker\'s pattern with specific infants and triggered an owner alert long before a leg fracture happened.' },
    ],
    note: 'The leg fracture may have been prevented. That\'s what early detection means in practice.',
  },
  {
    title: 'Little Blessings Child Care, Bainbridge, GA (2025)',
    period: 'Arrested August 11, 2025',
    image: '/brooklyn-daycare.png',
    summary: 'Yvette Thurston, 54, was arrested on August 11, 2025 and charged with aggravated battery and multiple counts of first-degree child abuse. The charges reflect a pattern of violent physical contact with children in her care.',
    acts: [
      'Aggravated battery against children in her care.',
      'Multiple counts of first-degree child abuse.',
      'Repeated violent physical contact documented across the facility.',
    ],
    outcomes: [
      'Three criminal counts filed including aggravated battery',
      'Arrested August 11, 2025; facility under investigation',
      'Multiple children affected across incidents',
      'Little Blessings Child Care facing closure and civil liability',
    ],
    source: { label: 'Source', url: '#' },
    prevention: [
      { act: 'Aggravated battery — violent physical contact', how: 'SMCLA\'s physical contact detection is trained to distinguish aggressive handling from normal childcare. The first battery incident would have triggered an immediate real-time alert.' },
      { act: 'Multiple counts across incidents', how: 'Each subsequent incident would have compounded the behavioral flag. By the second incident, SMCLA would have recommended removal from the floor pending investigation.' },
    ],
    note: 'Aggravated battery is not a subtle pattern — it\'s detectable on first occurrence. SMCLA would have stopped it before a second incident happened.',
  },
  {
    title: 'Blanding, Utah — The Case Cameras Solved Too Late',
    period: 'Incident Oct 27, 2025 → Arrest Jan 2, 2026',
    image: '/country-daycare.png',
    summary: 'Kyara Naomi Yazzie, 25, was physically aggressive toward a 2-year-old at a Blanding, Utah daycare. Security cameras captured the incident. She was arrested on January 2, 2026 — more than two months after the abuse. The cameras worked exactly as designed. That was the problem.',
    acts: [
      'Physical aggression against a 2-year-old child, captured on security footage.',
      'Footage sat unreviewed until someone decided to look — 2+ months later.',
      'Yazzie was arrested and charged with a Class A misdemeanor in January 2026.',
    ],
    outcomes: [
      'Arrest occurred 67 days after the incident',
      'Charged with Class A misdemeanor',
      'The 2-year-old victim had no protection on the day of the incident',
      'Camera footage was evidence — not prevention',
    ],
    source: { label: 'Source', url: '#' },
    callout: '"The cameras caught it. We would have stopped it." — SMCLA alert: within 60 seconds. Arrest: 67 days later.',
    prevention: [
      { act: 'Real-time detection vs. post-hoc review', how: 'SMCLA would have sent an owner alert within 60 seconds of the incident. Not 67 days. A manager could have intervened during the event — not months after.' },
      { act: 'Cameras record. SMCLA prevents.', how: 'This case is the definitive proof of the gap. Traditional CCTV is a documentation tool. SMCLA is a prevention system. The child needed prevention, not documentation.' },
    ],
    note: 'This case is why we exist. The cameras worked perfectly. And still failed the child completely.',
  },
  {
    title: 'Mather Childcare Center, Sacramento, CA (2025)',
    period: 'Arrested August 22, 2025',
    image: '/daycare-woman-child.png',
    summary: 'Gokhan Bagci, 26, was arrested by the Sheriff\'s Child Abuse Bureau on August 22, 2025. He faces four felony counts under Penal Code 288(b)(1) — lewd and lascivious acts on a child under 14 by force or fear. Among the most severe charge categories in California child abuse law.',
    acts: [
      'Four counts of lewd and lascivious acts on a child under 14 (PC 288(b)(1)).',
      'Acts alleged to have been committed by force or fear.',
      'Arrested by the Sheriff\'s Child Abuse Bureau following investigation.',
    ],
    outcomes: [
      'Four felony counts filed under California PC 288(b)(1)',
      'Arrested by Sheriff\'s Child Abuse Bureau, August 22, 2025',
      'Mather Childcare Center under investigation',
      'Multiple children potentially affected; investigation ongoing',
    ],
    source: { label: 'Source', url: '#' },
    prevention: [
      { act: 'Unauthorized physical isolation of a child', how: 'SMCLA detects when a worker moves a child away from shared spaces, cameras, or other staff — a primary behavioral precursor to this category of abuse. An immediate alert would have been triggered.' },
      { act: 'Unusual physical contact patterns', how: 'SMCLA\'s visual AI is specifically trained to flag contact patterns that fall outside the normal range for childcare worker behavior — enabling detection without compromising child privacy.' },
    ],
    note: 'SMCLA is visual-only — no audio recording, full privacy compliance. Yet it detects the behavioral signatures that precede the most serious abuse.',
  },
  {
    title: 'Punkin\' Doodles Day Care, Lindale, Texas (2024)',
    period: 'July – October 2024; arrested October 30, 2024; sentenced 2025',
    image: '/country-daycare.png',
    summary: 'Catherine Guziejka, 44, a worker at Punkin\' Doodles Day Care in Lindale, Texas, was recorded on facility cameras kicking six 2-year-old toddlers on 134 separate occasions over three months. She was also filmed standing on a child\'s left hand for seven seconds as punishment. The abuse was discovered only when a staff member accidentally found the footage and notified a victim\'s parent — not through any monitoring system. Guziejka pleaded guilty and was sentenced to 10 years in prison.',
    acts: [
      'Kicked six 2-year-old toddlers on 134 documented occasions over three months.',
      'Stood on children\'s hands as punishment — one instance lasted 7 seconds as the child tried to pull free.',
      'Aggressively laid children on the changing table, pushing their heads down and forcing them to face the wall.',
      'Repeatedly pushed toddlers.',
    ],
    outcomes: [
      'Arrested October 30, 2024; charged with multiple counts of injury to a child',
      'Pleaded guilty to injury to a child with intent to cause bodily injury',
      'Sentenced to 10 years in prison (2025)',
      'Six victims, all 2 years old',
      'Abuse discovered only after staff member accidentally reviewed footage — not through real-time detection',
    ],
    source: { label: 'Law&Crime article', url: 'https://lawandcrime.com/crime/an-ongoing-thing-for-no-reason-day-care-worker-caught-on-video-abusing-toddlers-134-times-over-several-months-is-headed-to-prison/' },
    prevention: [
      { act: '134 kick incidents captured — and missed — over 3 months', how: 'SMCLA\'s force and impact detection would have flagged the first kicking incident in real time. The abuse would have been stopped at incident 1, not discovered at incident 134.' },
      { act: 'Standing on a child\'s hand for 7 seconds', how: 'Abnormal physical contact and body positioning fall outside SMCLA\'s normal range model for childcare behavior. This act would have triggered an immediate alert, with timestamped clip evidence, before the child could be further harmed.' },
      { act: 'Discovered by accident — not by any system', how: 'A staff member happened to review footage and happened to tell a parent. That is not a safety system — that is luck. SMCLA removes luck from the equation. Pattern detection across 134 incidents would have surfaced this worker within the first shift.' },
    ],
    note: '134 incidents. Three months. Six victims. All on camera. None of it caught until someone stumbled across the footage. This is the exact gap SMCLA was built to close.',
  },
];

function CaseDetail({ c }: { c: typeof caseData[0] }) {
  return (
    <Box>
      {/* Image */}
      <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 4, border: '1px solid rgba(140,82,255,0.2)' }}>
        <img src={c.image} alt={c.title} style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block', filter: 'grayscale(0.1) contrast(1.05)' }} />
      </Box>

      {/* Title & period */}
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.6rem' }, color: '#8c52ff', mb: 0.5, lineHeight: 1.2 }}>
        {c.title}
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', mb: 2.5 }}>
        Timeline: {c.period}
      </Typography>

      {/* Callout (Blanding case) */}
      {'callout' in c && c.callout && (
        <Box sx={{ p: 3, bgcolor: 'rgba(92,225,230,0.05)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 2, mb: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#5ce1e6', fontStyle: 'italic' }}>
            {c.callout}
          </Typography>
        </Box>
      )}

      {/* Summary */}
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#d0d0e0', mb: 3, lineHeight: 1.7 }}>
        {c.summary}
      </Typography>

      {/* Acts */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#f0f0f8', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Alleged acts
        </Typography>
        <Stack spacing={1}>
          {c.acts.map((a, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#ff9f43', mt: 0.8, flexShrink: 0 }} />
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>{a}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Outcomes */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#f0f0f8', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Outcome
        </Typography>
        <Stack spacing={1}>
          {c.outcomes.map((o, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <ArrowForwardIcon sx={{ color: '#8c52ff', fontSize: 16, mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>{o}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Source */}
      {c.source.url !== '#' && (
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.6)', mb: 3 }}>
          <a href={c.source.url} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(92,225,230,0.7)', textDecoration: 'underline' }}>
            {c.source.label} ↗
          </a>
        </Typography>
      )}

      {/* SMCLA Prevention */}
      <Box sx={{ bgcolor: 'rgba(140,82,255,0.06)', border: '1px solid rgba(140,82,255,0.2)', borderRadius: 3, p: { xs: 3, md: 4 }, mt: 2 }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 13, color: '#8c52ff', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 3 }}>
          How SMCLA Could Have Prevented This
        </Typography>
        <Stack spacing={2.5}>
          {c.prevention.map(({ act, how }, i) => (
            <Box key={i}>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#d0d0e0', mb: 0.75 }}>
                {act}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>
                {how}
              </Typography>
              {i < c.prevention.length - 1 && <Divider sx={{ mt: 2.5, borderColor: 'rgba(140,82,255,0.12)' }} />}
            </Box>
          ))}
        </Stack>
        {'note' in c && c.note && (
          <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid rgba(140,82,255,0.15)' }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(140,82,255,0.9)', lineHeight: 1.65, fontStyle: 'italic' }}>
              {c.note}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function Why() {
  const [subPage, setSubPage] = React.useState(0);
  const [subExample, setSubExample] = React.useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [subPage]);

  const tabs = [
    { label: 'Real Stories', icon: <HistoryEduIcon sx={{ fontSize: 16 }} /> },
    { label: 'AI Technology', icon: <SmartToyIcon sx={{ fontSize: 16 }} /> },
    { label: 'Trends', icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
  ];

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Why AI Monitoring Can't Wait" description="Real cases where cameras existed but abuse went undetected for months. See why real-time behavioral AI is the only solution that works." path="/why" />
      <Header />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 8, md: 12 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(140,82,255,0.07) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(140,82,255,0.08)', border: '1px solid rgba(140,82,255,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <AutoAwesomeIcon sx={{ color: '#8c52ff', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#8c52ff', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Why It Matters
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '3.5rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2, lineHeight: 1.05 }}>
            Every week, a new story.<br />
            <Box component="span" sx={{ color: '#8c52ff' }}>None of them had to happen.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 15, md: 18 }, color: '#9499b0', maxWidth: 580, mx: 'auto', lineHeight: 1.65 }}>
            Children too young to speak bear invisible scars that last a lifetime. The heartbreak ripples out — parents lose trust, staff lose jobs, communities are shaken. SMCLA exists to close the gap between what happens and when someone finds out.
          </Typography>
        </Container>
      </Box>

      {/* Tab navigation */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: 2, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tabs.map(({ label, icon }, idx) => (
              <Button
                key={label}
                onClick={() => setSubPage(idx)}
                startIcon={icon}
                sx={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontWeight: 600,
                  fontSize: 13,
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  color: subPage === idx ? '#8c52ff' : '#9499b0',
                  bgcolor: subPage === idx ? 'rgba(140,82,255,0.08)' : 'transparent',
                  border: subPage === idx ? '1px solid rgba(140,82,255,0.25)' : '1px solid transparent',
                  '&:hover': { bgcolor: 'rgba(140,82,255,0.05)', color: '#d0d0e0' },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="md">

          {/* ── STORIES ── */}
          {subPage === 0 && (
            <Box>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 1 }}>
                Real Stories. Real Consequences.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 5, lineHeight: 1.7 }}>
                Each of these cases was avoidable. The patterns were there — they just had no system trained to see them.
              </Typography>

              {/* Case selector */}
              <Grid container spacing={1.5} sx={{ mb: 5 }}>
                {cases.map(({ title, sub, idx }) => (
                  <Grid key={title} size={{ xs: 6, sm: 4, md: 3 }}>
                    <Box
                      onClick={() => setSubExample(idx)}
                      sx={{
                        p: 2,
                        bgcolor: subExample === idx ? 'rgba(140,82,255,0.1)' : '#13131f',
                        border: subExample === idx ? '1px solid rgba(140,82,255,0.4)' : '1px solid rgba(255,255,255,0.06)',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: 'rgba(140,82,255,0.3)', bgcolor: 'rgba(140,82,255,0.06)' },
                      }}
                    >
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color: subExample === idx ? '#8c52ff' : '#f0f0f8', mb: 0.5, lineHeight: 1.3 }}>
                        {title}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: '#9499b0', lineHeight: 1.4 }}>
                        {sub}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Case detail */}
              <Box sx={{ p: { xs: 3, md: 5 }, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                <CaseDetail c={caseData[subExample]} />
              </Box>
            </Box>
          )}

          {/* ── AI TECHNOLOGY ── */}
          {subPage === 1 && (
            <Box>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
                <SmartToyIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  AI Technology
                </Typography>
              </Box>
              <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 2, lineHeight: 1.1 }}>
                AI Technology:<br />
                <Box component="span" sx={{ color: '#5ce1e6' }}>protecting staff and children.</Box>
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 6, maxWidth: 580 }}>
                For decades, keeping children safe in daycares meant relying on human vigilance — more staff, more supervision, more paperwork. AI changes the equation entirely.
              </Typography>

              {/* Image */}
              <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 6, border: '1px solid rgba(92,225,230,0.15)' }}>
                <img src="/ai-people-tech.png" alt="AI Technology" style={{ width: '100%', maxHeight: 400, objectFit: 'cover', display: 'block', filter: 'grayscale(0.1) contrast(1.05)' }} />
              </Box>

              {/* The old way */}
              <Box sx={{ mb: 5, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#ff9f43', mb: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  The old way
                </Typography>
                <Stack spacing={1}>
                  {[
                    'More staff, more supervision, more paperwork — all fallible',
                    'Humans get tired, distracted, or simply cannot see everything at once',
                    'Even the best staff miss warning signs that develop over weeks',
                    'Traditional monitoring is costly, error-prone, and impossible to scale',
                  ].map((p, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#ff9f43', mt: 0.8, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>{p}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* AI advantages */}
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.6rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 4 }}>
                Why AI is a game changer
              </Typography>
              <Grid container spacing={2.5} sx={{ mb: 5 }}>
                {[
                  { title: 'Never tired, never distracted', body: 'AI monitors every room, every moment, every shift — without fatigue, without looking away, without forgetting what it saw yesterday.' },
                  { title: 'Scales instantly', body: 'One system can monitor dozens of rooms simultaneously. A single deployment covers what would require dozens of human reviewers.' },
                  { title: 'Catches patterns humans miss', body: 'Behavioral drift across sessions, targeting patterns, escalating tone — these are invisible to spot checks. AI sees across time.' },
                  { title: 'Protects staff too', body: 'Tamper-evident, timestamped evidence protects good staff from false accusations. Objective evidence replaces he-said-she-said.' },
                  { title: 'From reactive to proactive', body: 'Cameras document what happened. AI prevents it. The shift from evidence to early warning is the entire value proposition.' },
                  { title: 'Improves over time', body: 'AI learns and adapts — finding patterns and incidents that would be impossible for any one person to catch over time.' },
                ].map(({ title, body }) => (
                  <Grid key={title} size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.08)', borderRadius: 3, height: '100%', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.2)' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <CheckCircleIcon sx={{ color: '#5ce1e6', fontSize: 16 }} />
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8' }}>{title}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>{body}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3, borderLeft: '3px solid #5ce1e6' }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 14, md: 16 }, color: '#d0d0e0', lineHeight: 1.8 }}>
                  Embracing AI is not just an upgrade — it is a necessity for modern child safety. With AI, we move from reactive crisis management to early prevention: protecting children, supporting staff, and safeguarding reputations before the headline ever happens.
                </Typography>
              </Box>
            </Box>
          )}

          {/* ── TRENDS ── */}
          {subPage === 2 && <TrendsPage embedded={true} />}

        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

export default Why;
